import React, { useEffect } from "react";
import { Organization, ProviderType, NewIntegration, Member } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import SelectInput from "../common/SelectInput";
import { AttentionIcon } from "../common/ProjectIcons";
import { useTranslation } from "react-i18next";
import { isActionVisible } from "../common/SchemaFormTemplates/checkSchema";
import { filterConfigurableActions } from "../common/SchemaFormTemplates/actionFilters";
import { getConnectionKind } from "../common/connectionKind";
import { getButtonText } from "../common/TestActionFunctions";
import { Tooltip } from "../common/Tooltip";
import { buttonClasses, containerClasses, inputClasses } from "../common/tailwindClassnames";
import { useHelp, HelpCard } from "../../context/HelpContext";

type NewConnectionStepTwoProps = {
    currentMemberIsAdmin: boolean,
    initialConnection: NewIntegration,
    errors: any,
    rsjfErrors: any,
    selectedProviderType: ProviderType,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChangeSchemaForm: Function,
    onChangeName: Function,
    onSelectOwner: Function,
}

const NewConnectionStepTwo = ({
    currentMemberIsAdmin,
    initialConnection,
    errors,
    rsjfErrors,
    selectedProviderType,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChangeSchemaForm,
    onChangeName,
    onSelectOwner,
}: NewConnectionStepTwoProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();

    useEffect(() => {
        const kind = getConnectionKind(selectedProviderType);
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Connection in Gundi'),
                description: t('Find step-by-step instructions for connecting your data source.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/' },
            },
            ...(selectedProviderType.help_center_url ? [{
                title: t('{{technology}} Connection Set Up', { technology: selectedProviderType.name }),
                description: t('Configuration guide specific to {{technology}} connections.', { technology: selectedProviderType.name }),
                button: { label: t('Learn More'), url: selectedProviderType.help_center_url },
            }] : []),
            ...(kind === 'webhook' ? [{
                title: t('Gundi Webhook API'),
                description: t('Learn how to send data to Gundi using webhooks.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/custom-webhook' },
            }] : []),
            ...(kind === 'push-only' ? [{
                title: t('Gundi API'),
                description: t('Learn how to push data to Gundi using the API.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/gundi-api' },
            }] : []),
        ];
        setHelpContent({ cards });
    }, [selectedProviderType, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);


    return (
        <div className="mt-8 w-full">
            <div className="flex flex-col pb-16 w-full">
                {/* form errors */}
                {(Object.keys(errors).length > 0 || Object.keys(rsjfErrors).length > 0) &&
                    <div className="bg-very-light-orange py-2 px-3 rounded-sm mb-6 text-dark-gray transition-colors duration-300">
                        <AttentionIcon /> {t("There are fields that require your attention. Please check and correct the errors.")}
                    </div>}
                {/* connection organization and connection name box */}
                <div className="rounded p-[16px] mb-[16px] transition-colors duration-300">
                    <label className="text-off-black text-lg mt-8 transition-colors duration-300">
                        {t("Organize this connection. Consider adding a name that will help differentiate this connection from another.")}
                    </label>
                    <div className="mt-3 flex flex-row">
                        {/* organization */}
                        <div className="flex flex-col w-1/2 mr-3 mt-1 pb-1">
                            <label
                                className="inline-block hover:cursor-pointer text-sm text-secondary-gray transition-colors duration-300"
                            >
                                {t("Connection Workspace")} *
                            </label>
                            <SelectInput
                                options={organizations.map((org: Organization) => {
                                    return {
                                        label: org.name,
                                        value: org.id
                                    }
                                })}
                                className={"basic-single"}
                                autoSelectSingleOption={true}
                                isDisabled={false}
                                isLoading={false}
                                isClearable={false}
                                isSearchable={true}
                                isMulti={false}
                                name={t("Workspace")}

                                onChange={(e: any) => onSelectOwner(e)}
                                onMenuScrollToBottom={loadMoreOrgs}
                                handleInputChange={(e: any) => orgInputChange(e)}
                            />
                            {errors.owner && <div className="text-orange-600 py-1 rounded flex flex-row items-center transition-colors duration-300"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                        </div>
                        {/* name */}
                        <div className="flex flex-col w-1/2 mr-3 mt-1">
                            <label
                                className="inline-block hover:cursor-pointer text-sm text-secondary-gray transition-colors duration-300"
                            >
                                {t("Connection Name")} *
                            </label>
                            <input
                                id="name"
                                data-testid="NewConnectionStepTwo-name-input"
                                placeholder=""
                                value={initialConnection.name}
                                className={inputClasses.standard}

                                onChange={(e) => onChangeName(e)}
                                disabled={false}
                            >
                            </input>
                            {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center transition-colors duration-300"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                        </div>
                    </div>
                </div>

                {/* configuration actions box */}
                {selectedProviderType.actions &&
                    selectedProviderType.actions.length > 0 &&
                    selectedProviderType.actions.some((item: any) =>
                        item.schema &&
                        Object.keys(item.schema).length > 0
                    ) && (
                        <div className="rounded p-[16px] transition-colors duration-300">
                            <div className="flex flex-row items-center justify-between w-full mb-[16px]">
                                <label className="text-off-black text-lg transition-colors duration-300">{t("Enter the following information to connect to your data provider")}</label>
                            </div>
                            <div className="flex flex-col w-full mr-3">
                                {/* schema form */}
                                {filterConfigurableActions(selectedProviderType.actions, "push").map((item: any) => {
                                    const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                                    const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                                    const hasSchema = isActionVisible(item);
                                    return (
                                        <div
                                            key={item.id}
                                            tabIndex={-1}
            
                                            className={hasSchema ? "mb-[16px] rounded p-[16px]" : "hidden"}
                                        >
                                            {hasSchema && (
                                                <>
                                                    <div className="mb-[16px] flex flex-col items-start">
                                                        <div className="flex flex-row items-center justify-between w-full">
                                                            <div className="text-2xl font-normal text-secondary-gray transition-all duration-300">{formTitle}</div>
                                                            {item.schema?.is_executable === true && (
                                                                <div className="flex flex-row items-center">
                                                                    <button disabled className={`${buttonClasses.lightGreen} ml-1 mr-3`}>
                                                                        {getButtonText(item.value, t)}
                                                                    </button>
                                                                    <Tooltip description={t("Connections can be tested after the setup workflow is complete.")} position="right" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        {uiSchema['ui:description'] && (
                                                            <div className="form-root-description !mb-0 text-gray-600 mt-1 transition-colors duration-300">
                                                                {uiSchema['ui:description']}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {item.schema?.properties && Object.keys(item.schema.properties).length > 0 && <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                                                        <SchemaForm
                                                            userIsAdmin={currentMemberIsAdmin}
                                                            formTitle={''}
                                                            schema={item.schema}
                                                            ui_schema={{...uiSchema, 'ui:description': ''}}
                                                            onChange={(e: any) => onChangeSchemaForm(e, item.id)}
                                                            isEditable={true}
                                                            errors={rsjfErrors[item.id]}
                                                        />
                                                    </div>}
                                                </>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}

            </div>
        </div>
    );
};

export default NewConnectionStepTwo;